const root = state => state.lessonContentViewer;
const SYSTEM_TUTORIAL_AUDIO_ID = 'system.tutorial';
const SYSTEM_KC_INTRO_AUDIO_ID = 'system.knowledge_check_intro';
const SYSTEM_KC_SUMMARY_AUDIO_ID = 'system.knowledge_check_summary';
const SYSTEM_MASTERY_AUDIO_ID = 'system.mastery_intro';
const SYSTEM_RESULTS_PASSED_AUDIO_ID = 'system.results.passed';
const SYSTEM_RESULTS_FAILED_AUDIO_ID = 'system.results.failed';

function createAudioTarget(audioId, text) {
  const normalizedText = String(text || '').trim();
  if (!audioId || !normalizedText) return null;
  return {audioId, text: normalizedText};
}

function compactTargets(targets) {
  return targets.filter(Boolean);
}

function getLearningStepAtIndex(term, index) {
  const steps = term?.learningSteps || [];
  if (steps[index]) return steps[index];
  if (index === 0) return term?.learning || null;
  return null;
}

function getWorkbookExerciseAtIndex(term, index) {
  const exercises = term?.workbook?.exercises || [];
  if (exercises[index]) return exercises[index];
  if (index === 0) return term?.workbook || null;
  return null;
}

function getLearningTarget(term, stepIndex = 0) {
  const step = getLearningStepAtIndex(term, stepIndex);
  return createAudioTarget(step?.tutorAudioId, step?.tutorScript);
}

function getWorkbookOpenTarget(workbook) {
  return createAudioTarget(
    workbook?.tutorOpenWorkbookAudioId,
    workbook?.tutorOpenWorkbook,
  );
}

function getWorkbookReadTarget(workbook) {
  return createAudioTarget(
    workbook?.tutorReadExerciseAudioId,
    workbook?.tutorReadExercise,
  );
}

function getWorkbookOptionsTarget(workbook) {
  return createAudioTarget(
    workbook?.tutorPresentOptionsAudioId,
    workbook?.tutorPresentOptions,
  );
}

function getWorkbookFeedbackTarget(term, workbook, selectedOption) {
  if (selectedOption == null) return null;
  const feedback = workbook?.optionFeedback?.[selectedOption];
  const audioId =
    workbook?.optionFeedbackAudioIds?.[selectedOption] ||
    `system.workbook_feedback.${term?.sequenceIndex ?? 0}.${selectedOption}`;

  return createAudioTarget(audioId, feedback);
}

function getKnowledgeCheckReaction(correctCount) {
  if (correctCount <= 0) return 'Oh...';
  if (correctCount === 1) return 'Good effort.';
  return 'Great work!';
}

function getKnowledgeCheckIntroText() {
  return "It's time for a test!";
}

function getKnowledgeCheckSummaryText(state) {
  const score = selectKCScore(state);
  const term = selectActiveTerm(state);
  const reaction = getKnowledgeCheckReaction(score.correct);
  const transition = String(term?.knowledgeCheck?.tutorTransition || '').trim();
  const scoreText = `${reaction} You got ${score.correct} out of ${score.total} correct.`;

  return transition ? `${scoreText} ${transition}` : scoreText;
}

function getKnowledgeCheckIntroTarget(term) {
  return createAudioTarget(
    `${SYSTEM_KC_INTRO_AUDIO_ID}.${term?.sequenceIndex ?? 0}`,
    getKnowledgeCheckIntroText(),
  );
}

function getKCSummaryTarget(state) {
  const score = selectKCScore(state);
  const text = getKnowledgeCheckSummaryText(state);
  return createAudioTarget(
    `${SYSTEM_KC_SUMMARY_AUDIO_ID}.${root(state).activeTermIndex}.${score.correct}.${score.total}`,
    text,
  );
}

function getMasteryIntroTarget() {
  return createAudioTarget(
    SYSTEM_MASTERY_AUDIO_ID,
    'Time for the mastery test! Show me what you\'ve learned.',
  );
}

function getResultsTarget(state) {
  const mScore = selectMasteryScore(state);
  const text = mScore.passed
    ? 'Excellent work! You\'ve mastered this lesson!'
    : 'Good effort! Keep practicing to improve your score.';
  return createAudioTarget(
    mScore.passed ? SYSTEM_RESULTS_PASSED_AUDIO_ID : SYSTEM_RESULTS_FAILED_AUDIO_ID,
    text,
  );
}

function getTermAtIndex(state, index) {
  return root(state).viewerModel?.terms?.[index] || null;
}

export const selectViewerLoading = state => root(state).loading;
export const selectLessonViewerUnityWorldLoading = state =>
  root(state).unityWorldLoading;
export const selectViewerError = state => root(state).error;
export const selectViewerModel = state => root(state).viewerModel;
export const selectRawLessonPayload = state => root(state).rawPayload;
export const selectCurrentPhase = state => root(state).phase;
export const selectActiveTermIndex = state => root(state).activeTermIndex;
export const selectActiveQuestionIndex = state => root(state).activeQuestionIndex;
export const selectActiveLearningStepIndex = state =>
  root(state).activeLearningStepIndex;
export const selectActiveWorkbookExerciseIndex = state =>
  root(state).activeWorkbookExerciseIndex;
export const selectShowDevTools = state => root(state).showDevTools;
export const selectTutorChatOpen = state => root(state).tutorChatOpen;
export const selectTutorialViewed = state => root(state).tutorialViewed;
export const selectNarrationStatus = state => root(state).narrationStatus;
export const selectNarrationError = state => root(state).narrationError;
export const selectNarrationAudioId = state => root(state).narrationAudioId;

export const selectMetadata = state => root(state).viewerModel?.metadata || null;

export const selectActiveTerm = state => {
  const model = root(state).viewerModel;
  const idx = root(state).activeTermIndex;
  return model?.terms?.[idx] || null;
};

export const selectActiveTermCard = state => {
  const term = selectActiveTerm(state);
  if (!term) return null;
  return {
    term: term.term,
    definition: term.definition,
    exampleSentence: term.exampleSentence,
  };
};

export const selectActiveLearningStep = state => {
  const term = selectActiveTerm(state);
  const idx = selectActiveLearningStepIndex(state);
  return getLearningStepAtIndex(term, idx);
};

export const selectActiveWorkbook = state => {
  const term = selectActiveTerm(state);
  const idx = selectActiveWorkbookExerciseIndex(state);
  const workbook = term?.workbook || null;
  const exercise = getWorkbookExerciseAtIndex(term, idx);
  if (!workbook || !exercise) return workbook;

  return {
    ...workbook,
    ...exercise,
    tutorOpenWorkbook: workbook.tutorOpenWorkbook || '',
    tutorOpenWorkbookAudioId: workbook.tutorOpenWorkbookAudioId || null,
    exercises: workbook.exercises || [],
    primaryExerciseIndex: workbook.primaryExerciseIndex ?? 0,
  };
};

export const selectWorkbookState = state => ({
  ready: root(state).workbookReady,
  done: root(state).workbookDone,
  submitted: root(state).workbookSubmitted,
  selectedOption: root(state).workbookSelectedOption,
});

export const selectActiveKCQuestions = state => {
  const term = selectActiveTerm(state);
  return term?.knowledgeCheck?.questions || [];
};

export const selectActiveKCQuestion = state => {
  const questions = selectActiveKCQuestions(state);
  const idx = root(state).activeQuestionIndex;
  return questions[idx] || null;
};

export const selectKCAnswers = state => root(state).kcAnswers;

export const selectKCScore = state => {
  const answers = root(state).kcAnswers;
  return {
    correct: answers.filter(a => a.correct).length,
    total: answers.length,
  };
};

export const selectMasteryQuestions = state =>
  root(state).viewerModel?.mastery?.questions || [];

export const selectActiveMasteryQuestion = state => {
  const questions = selectMasteryQuestions(state);
  const idx = root(state).activeQuestionIndex;
  return questions[idx] || null;
};

export const selectMasteryAnswers = state => root(state).masteryAnswers;

export const selectMasteryScore = state => {
  const answers = root(state).masteryAnswers;
  const threshold = root(state).viewerModel?.mastery?.passingThreshold || 0.8;
  const correct = answers.filter(a => a.correct).length;
  const total = answers.length;
  const ratio = total > 0 ? correct / total : 0;
  return {
    correct,
    total,
    passed: ratio >= threshold,
    ratio,
    threshold,
  };
};

export const selectMasteryComplete = state => root(state).masteryComplete;
export const selectSessionStats = state => root(state).sessionStats;

export const selectFrontloadingData = state => {
  const model = root(state).viewerModel;
  if (!model) return null;
  return {
    tutorScript: model.frontloading.tutorScript,
    termOrder: model.frontloading.termOrder,
    terms: model.terms,
    estimatedDurationMinutes: model.metadata.estimatedDurationMinutes,
    topic: model.metadata.topic,
  };
};

export const selectKeyTermProgressMap = state => root(state).keyTermProgress || {};

/**
 * Rank the persistent statuses returned by GET /key-term-progress so we
 * can compare them like a high-water mark.
 *   - workbook         → LEARN done
 *   - knowledge_check  → LEARN + WORK done
 *   - completed        → LEARN + WORK + CHECK done
 */
const TERM_STATUS_RANK = {
  not_started: 0,
  learning: 0,
  workbook: 1,
  knowledge_check: 2,
  completed: 3,
};

function statusRank(status) {
  return TERM_STATUS_RANK[String(status || '').toLowerCase()] ?? 0;
}

export const selectProgressTrackerData = state => {
  const model = root(state).viewerModel;
  const s = root(state);
  if (!model) return null;

  const phase = s.phase;
  const introComplete = phase !== 'tutorial' && phase !== 'frontloading';
  const masteryReached = ['mastery', 'results'].includes(phase);

  let activeSubPhase = null;
  if (['learning'].includes(phase)) activeSubPhase = 'learning';
  else if (
    ['workbook_ready', 'workbook_exercise', 'workbook_options'].includes(phase)
  )
    activeSubPhase = 'workbook';
  else if (['knowledge_check', 'knowledge_check_summary'].includes(phase))
    activeSubPhase = 'knowledge_check';

  const keyTermProgress = s.keyTermProgress || {};

  // Per-term completion rank comes solely from the persisted
  // key-term-progress map (which is also updated optimistically on each
  // sub-phase transition — see the PUT /key-term-progress effect in
  // LessonContentViewer). We deliberately do NOT infer completion from
  // `activeTermIndex` or the current sub-phase: doing so would mark every
  // earlier term as "done" the moment the learner clicks forward in the
  // tracker, even when they never actually finished those sections.
  const termRanks = model.terms.map(term =>
    statusRank(keyTermProgress[String(term.term || '').trim().toLowerCase()]),
  );

  // termsDone = number of terms whose CHECK step is checked. Used by the
  // tracker header ("X of Y terms done"). Reflects real completion only.
  const termsDone = termRanks.filter(rank => rank >= 3).length;

  return {
    terms: model.terms.map(t => ({term: t.term})),
    activeTermIndex: s.activeTermIndex,
    activeSubPhase,
    introComplete,
    masteryReached,
    termsDone,
    termRanks,
    stats: s.sessionStats,
  };
};

export const selectTutorText = state => {
  const s = root(state);
  const term = selectActiveTerm(state);
  const learningStep = selectActiveLearningStep(state);
  const workbook = selectActiveWorkbook(state);
  const model = s.viewerModel;

  switch (s.phase) {
    case 'tutorial':
      return 'Welcome! Let me show you how this lesson works.';
    case 'frontloading':
      return model?.frontloading?.tutorScript || '';
    case 'learning':
      return learningStep?.tutorScript || '';
    case 'workbook_ready':
      return s.workbookReady
        ? workbook?.tutorReadExercise || ''
        : workbook?.tutorOpenWorkbook || '';
    case 'workbook_exercise':
      return workbook?.tutorReadExercise || '';
    case 'workbook_options':
      if (s.workbookSubmitted && s.workbookSelectedOption != null) {
        const feedback = workbook?.optionFeedback || [];
        return feedback[s.workbookSelectedOption] || '';
      }
      return workbook?.tutorPresentOptions || '';
    case 'knowledge_check':
      if (s.kcAnswers.length === 0) {
        return getKnowledgeCheckIntroText();
      }
      return '';
    case 'knowledge_check_summary': {
      return getKnowledgeCheckSummaryText(state);
    }
    case 'mastery':
      return 'Time for the mastery test! Show me what you\'ve learned.';
    case 'results': {
      const mScore = selectMasteryScore(state);
      return mScore.passed
        ? 'Excellent work! You\'ve mastered this lesson!'
        : 'Good effort! Keep practicing to improve your score.';
    }
    default:
      return '';
  }
};

export const selectCurrentTutorAudio = state => {
  const s = root(state);
  if (!s.viewerModel) return null;
  const term = selectActiveTerm(state);
  const learningStep = selectActiveLearningStep(state);
  const workbook = selectActiveWorkbook(state);

  switch (s.phase) {
    case 'tutorial':
      return createAudioTarget(
        SYSTEM_TUTORIAL_AUDIO_ID,
        'Welcome! Let me show you how this lesson works.',
      );
    case 'frontloading':
      return createAudioTarget(
        s.viewerModel?.frontloading?.tutorAudioId || 'frontloading.tutorScript',
        s.viewerModel?.frontloading?.tutorScript,
      );
    case 'learning':
      return createAudioTarget(learningStep?.tutorAudioId, learningStep?.tutorScript);
    case 'workbook_ready':
      return s.workbookReady
        ? getWorkbookReadTarget(workbook)
        : getWorkbookOpenTarget(workbook);
    case 'workbook_exercise':
      return getWorkbookReadTarget(workbook);
    case 'workbook_options':
      if (s.workbookSubmitted && s.workbookSelectedOption != null) {
        return getWorkbookFeedbackTarget(term, workbook, s.workbookSelectedOption);
      }
      return getWorkbookOptionsTarget(workbook);
    case 'knowledge_check':
      if (s.kcAnswers.length > 0) return null;
      return getKnowledgeCheckIntroTarget(term);
    case 'knowledge_check_summary':
      return getKCSummaryTarget(state);
    case 'mastery':
      if (s.activeQuestionIndex > 0 || s.masteryAnswers.length > 0) {
        return null;
      }
      return getMasteryIntroTarget();
    case 'results':
      return getResultsTarget(state);
    default:
      return null;
  }
};

export const selectUpcomingTutorAudios = state => {
  const s = root(state);
  const term = selectActiveTerm(state);
  const nextTerm = getTermAtIndex(state, s.activeTermIndex + 1);
  const workbook = selectActiveWorkbook(state);
  const nextLearningStep = getLearningStepAtIndex(
    term,
    s.activeLearningStepIndex + 1,
  );
  const nextWorkbookExercise = getWorkbookExerciseAtIndex(
    term,
    s.activeWorkbookExerciseIndex + 1,
  );

  switch (s.phase) {
    case 'tutorial':
      return compactTargets([
        createAudioTarget(
          s.viewerModel?.frontloading?.tutorAudioId || 'frontloading.tutorScript',
          s.viewerModel?.frontloading?.tutorScript,
        ),
        getLearningTarget(selectActiveTerm(state), 0),
      ]).slice(0, 2);
    case 'frontloading':
      return compactTargets([
        getLearningTarget(term, 0),
        getLearningTarget(term, 1) || getWorkbookOpenTarget(workbook),
      ]).slice(0, 2);
    case 'learning':
      return compactTargets([
        nextLearningStep
          ? createAudioTarget(
              nextLearningStep.tutorAudioId,
              nextLearningStep.tutorScript,
            )
          : getWorkbookOpenTarget(workbook),
        nextLearningStep
          ? getWorkbookOpenTarget(workbook)
          : getWorkbookReadTarget(workbook),
      ]).slice(0, 2);
    case 'workbook_ready':
      return compactTargets(
        s.workbookReady
          ? [
              getWorkbookOptionsTarget(workbook),
              nextWorkbookExercise
                ? getWorkbookOpenTarget({
                    ...term?.workbook,
                    ...nextWorkbookExercise,
                  })
                : getKnowledgeCheckIntroTarget(term),
            ]
          : [
              getWorkbookReadTarget(workbook),
              getWorkbookOptionsTarget(workbook),
            ],
      ).slice(0, 2);
    case 'workbook_exercise':
      return compactTargets([
        getWorkbookOptionsTarget(workbook),
        nextWorkbookExercise
          ? getWorkbookOpenTarget({
              ...term?.workbook,
              ...nextWorkbookExercise,
            })
          : getKnowledgeCheckIntroTarget(term),
      ]).slice(0, 2);
    case 'workbook_options':
      return compactTargets([
        nextWorkbookExercise
          ? getWorkbookOpenTarget({
              ...term?.workbook,
              ...nextWorkbookExercise,
            })
          : getKnowledgeCheckIntroTarget(term),
        nextWorkbookExercise
          ? getWorkbookReadTarget({
              ...term?.workbook,
              ...nextWorkbookExercise,
            })
          : getKCSummaryTarget(state),
      ]).slice(0, 2);
    case 'knowledge_check':
      if (s.kcAnswers.length > 0) return [];
      return compactTargets([
        getKCSummaryTarget(state),
        nextTerm ? getLearningTarget(nextTerm, 0) : getMasteryIntroTarget(),
      ]).slice(0, 2);
    case 'knowledge_check_summary':
      return compactTargets(
        nextTerm
          ? [
              getLearningTarget(nextTerm, 0),
              getWorkbookOpenTarget(getWorkbookExerciseAtIndex(nextTerm, 0) ? {
                ...nextTerm?.workbook,
                ...getWorkbookExerciseAtIndex(nextTerm, 0),
              } : nextTerm?.workbook),
            ]
          : [
              getMasteryIntroTarget(),
              getResultsTarget(state),
            ],
      ).slice(0, 2);
    case 'mastery':
      return compactTargets([getResultsTarget(state)]).slice(0, 2);
    default:
      return [];
  }
};

export const selectNarrationTargets = state => {
  const current = selectCurrentTutorAudio(state);
  return current
    ? [current, ...selectUpcomingTutorAudios(state)].slice(0, 3)
    : [];
};

export const selectIsNarrationGateOpen = state => {
  const currentAudio = selectCurrentTutorAudio(state);
  if (!currentAudio) return true;

  const narrationAudioId = selectNarrationAudioId(state);
  const narrationStatus = selectNarrationStatus(state);

  if (currentAudio.audioId !== narrationAudioId) {
    return false;
  }

  return narrationStatus === 'complete' || narrationStatus === 'error';
};
